import { Trash } from "@phosphor-icons/react";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import useMicrophonePermission from "common/hooks/useMicrophonePermission";

import { MIME_TYPES } from "../../../constants";
import { extractWaveform } from "../../../utils";
import MicrophonePermissionNotice from "./MicrophonePermissionNotice";
import PlaybackControls from "./PlaybackControls";
import RecordingControls from "./RecordingControls";
import TimerDisplay from "./TimerDisplay";
import WaveformCanvas from "./WaveformCanvas";

interface VoiceRecorderProps {
  onAudioReady: (audioBlob: Blob | null) => void;
  onRecordingDelete: () => void;
  autoStart?: boolean;
}

const MAX_RECORDING_TIME = 60;

export default function VoiceRecorder({
  onAudioReady,
  onRecordingDelete,
  autoStart = false,
}: VoiceRecorderProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const animationRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const hasAutoStartedRef = useRef(false);

  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [timer, setTimer] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [waveform, setWaveform] = useState<number[] | null>(null);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);

  const { permissionStatus, isRequestingPermission, requestPermission } =
    useMicrophonePermission();

  useEffect(() => {
    if (audioBlob) onAudioReady(audioBlob);
  }, [audioBlob]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      setIsPlaying(false);
      setPlaybackTime(0);
    };
    const handleLoadedMetadata = () => {
      if (audioRef.current && !Number.isNaN(audioRef.current.duration)) {
        setAudioDuration(audioRef.current.duration);
      } else {
        setAudioDuration(0);
      }
    };
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);

    // If metadata is already loaded, set duration
    if (audio.readyState >= 1 && !Number.isNaN(audio.duration)) {
      setAudioDuration(audio.duration);
    }

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [audioUrl]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "recording"
      ) {
        mediaRecorderRef.current.stop();
      }
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    },
    [],
  );

  const startRecording = useCallback(async () => {
    setAudioUrl(null);
    setAudioBlob(null);
    setWaveform(null);
    setPlaybackTime(0);
    setIsPlaying(false);

    if (permissionStatus === "denied") {
      await requestPermission();

      if (permissionStatus === "denied") {
        return;
      }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const supportedMimeType = MIME_TYPES.find(({ mimeType }) =>
        MediaRecorder.isTypeSupported(mimeType),
      );

      const mediaRecorder = new MediaRecorder(
        stream,
        supportedMimeType
          ? { mimeType: supportedMimeType.mimeType }
          : undefined,
      );

      mediaRecorderRef.current = mediaRecorder;
      audioChunks.current = [];

      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();

      analyserRef.current = analyser;
      source.connect(analyser);
      analyser.fftSize = 2048;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      let lastDrawTime = 0;

      const draw = (now?: number) => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        if (!ctx) return;

        const currentTime = now || performance.now();

        if (currentTime - lastDrawTime > 50) {
          analyser.getByteTimeDomainData(dataArray);
          lastDrawTime = currentTime;
        }

        animationRef.current = requestAnimationFrame(draw);
      };

      draw();

      mediaRecorder.ondataavailable = (e) => {
        audioChunks.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        if (animationRef.current) cancelAnimationFrame(animationRef.current);

        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }

        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        const blob = new Blob(audioChunks.current, {
          type: supportedMimeType?.mimeType,
        });

        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));

        const waveformData = await extractWaveform(
          blob,
          43, // 216px / 5 = 43 bars (width)
          27, // canvas height
        );
        setWaveform(waveformData);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      setIsRecording(false);
    }
  }, [extractWaveform, permissionStatus, requestPermission]);

  useEffect(() => {
    if (!hasAutoStartedRef.current && autoStart) {
      hasAutoStartedRef.current = true;
      startRecording();
    }
  }, []);

  const stopRecording = useCallback(() => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  }, []);

  const pauseAllOtherAudios = useCallback((currentAudio: HTMLAudioElement) => {
    const allAudios = document.querySelectorAll("audio");
    for (let i = 0; i < allAudios.length; i += 1) {
      const audioElement = allAudios[i];
      if (audioElement !== currentAudio && !audioElement.paused) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    }
  }, []);

  const handlePlay = useCallback(() => {
    if (audioRef.current) {
      pauseAllOtherAudios(audioRef.current);
      setIsPlaying(true);
      setHasPlayed(true);

      audioRef.current.play();
    }
  }, [pauseAllOtherAudios]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    audioRef.current?.pause();
  }, []);

  const handleDelete = useCallback(() => {
    setAudioUrl(null);
    setAudioBlob(null);
    setWaveform(null);
    setTimer(0);
    setPlaybackTime(0);

    onAudioReady(null);
    onRecordingDelete();
  }, [onAudioReady]);

  useEffect(() => {
    let interval: number | undefined;

    if (isRecording) {
      setTimer(0);
      interval = window.setInterval(() => {
        setTimer((t) => {
          if (t + 1 >= MAX_RECORDING_TIME) {
            stopRecording();
            return MAX_RECORDING_TIME;
          }
          return t + 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) window.clearInterval(interval);
    };
  }, [isRecording, stopRecording]);

  const shouldShowPermissionNotice =
    permissionStatus === "denied" || permissionStatus === "prompt";

  if (shouldShowPermissionNotice) {
    return (
      <MicrophonePermissionNotice
        status={permissionStatus}
        isRequesting={isRequestingPermission}
      />
    );
  }

  let displayPlaybackTime = 0;

  if (isRecording) {
    displayPlaybackTime = timer;
  } else if (audioUrl && audioRef.current) {
    // Use audioDuration from state, fallback to 0 if NaN
    const duration = !Number.isNaN(audioDuration) ? audioDuration : 0;
    // If never played, show duration
    if (!hasPlayed) {
      displayPlaybackTime = duration;
    } else if (isPlaying || playbackTime > 0) {
      // If playing or paused, show current position
      displayPlaybackTime = playbackTime;
    } else {
      // fallback to duration
      displayPlaybackTime = duration;
    }
  }

  return (
    <div className="flex w-full flex-row-reverse items-center justify-between gap-x-1 lg:w-fit lg:flex-row lg:justify-start lg:gap-x-2">
      <div
        className={clsx([
          "flex w-full flex-row items-center justify-between rounded-full p-2 pr-3 lg:w-fit lg:justify-center",
          isRecording ? "bg-purple-500" : "bg-purple-50",
          timer || isRecording ? "gap-x-2" : "gap-x-1",
        ])}
      >
        <RecordingControls
          isRecording={isRecording}
          hasAudio={!!audioUrl}
          onStartRecording={startRecording}
          onStopRecording={stopRecording}
        />

        <RenderIf condition={!!audioUrl && !isRecording}>
          <PlaybackControls
            isPlaying={isPlaying}
            onPlay={handlePlay}
            onPause={handlePause}
          />
        </RenderIf>

        <WaveformCanvas
          isRecording={isRecording}
          hasAudio={!!audioUrl}
          waveform={waveform}
          isPlaying={isPlaying}
          playbackTime={playbackTime}
          analyser={isRecording ? analyserRef.current : undefined}
          duration={audioRef.current ? audioRef.current.duration : 0}
          currentTime={
            isPlaying && audioRef.current
              ? audioRef.current.currentTime
              : playbackTime
          }
        />

        {isRecording || audioUrl ? (
          <TimerDisplay isRecording={isRecording} timer={displayPlaybackTime} />
        ) : (
          <span className="text-sm text-black-500">Start recording</span>
        )}

        <RenderIf condition={!!audioUrl}>
          <audio
            ref={audioRef}
            src={audioUrl}
            className="sr-only"
            onPlay={() => {
              setIsPlaying(true);
              setHasPlayed(true);
            }}
            onPause={() => {
              setIsPlaying(false);
              if (audioRef.current) {
                setPlaybackTime(audioRef.current.currentTime);
              }
            }}
            onTimeUpdate={() => {
              if (audioRef.current) {
                setPlaybackTime(audioRef.current.currentTime);
              }
            }}
          >
            <track kind="captions" />
          </audio>
        </RenderIf>
      </div>

      <RenderIf condition={audioUrl && !isRecording}>
        <Button
          size="sm"
          intent="iconOnly"
          variant="secondary-black"
          onClick={handleDelete}
          aria-label="Delete recording"
        >
          <Trash />
        </Button>
      </RenderIf>
    </div>
  );
}
